import { Injectable, computed, inject } from '@angular/core';
import type { CancellationReason } from '../contracts/subscribe-request.contract';
import type {
  CancelResponseContract,
  SubscribeResponseContract,
} from '../contracts/subscribe-response.contract';
import type { BillingHttpError } from '../models/billing-errors';
import type { PlanCatalog } from '../models/plan-catalog.model';
import type { PaymentMethod, PlanSlug, SubscriptionStatus } from '../models/subscription-status.enum';
import { BillingApiService } from '../services/billing-api.service';
import { daysBetween, resolveTrialColor, shouldShowUrgencyBanner, type TrialProgressionColor } from '../utils/trial-progression.util';
import { BillingStore } from './billing.store';
import { PlansStore } from './plans.store';

export interface BillingActionResult {
  ok: boolean;
  error?: BillingHttpError;
}

export interface BillingCancelResult extends BillingActionResult {
  response?: CancelResponseContract;
}

export interface BillingReactivateResult extends BillingActionResult {
  response?: SubscribeResponseContract;
}

export interface ReactivateRequest {
  paymentMethod: PaymentMethod;
  cardToken?: string;
}

@Injectable({ providedIn: 'root' })
export class BillingFacade {
  private readonly store = inject(BillingStore);
  private readonly api = inject(BillingApiService);
  private readonly plansStore = inject(PlansStore);

  readonly subscription = this.store.subscription;
  readonly usage = this.store.usage;
  readonly invoices = this.store.invoices;
  readonly loading = this.store.loading;
  readonly error = this.store.error;

  readonly status = computed<SubscriptionStatus | null>(() => this.subscription()?.status ?? null);
  readonly planSlug = computed(() => this.subscription()?.planSlug ?? null);
  readonly planTier = computed(() => this.subscription()?.planTier ?? null);
  readonly planName = computed(() => this.subscription()?.planName ?? '');
  readonly pendingCharge = computed(() => this.subscription()?.pendingCharge ?? null);
  readonly scheduledPlanChange = computed(() => this.subscription()?.scheduledPlanChange ?? null);
  readonly statusMessage = computed(() => this.subscription()?.statusMessage ?? null);

  readonly isTrialing = computed(() => this.status() === 'Trialing');
  readonly isActive = computed(() => this.status() === 'Active');
  readonly isPastDue = computed(() => this.status() === 'PastDue');
  readonly isCanceled = computed(() => this.status() === 'Canceled');
  readonly isExpired = computed(() => this.status() === 'Expired');
  readonly isPending = computed(() => this.status() === 'Pending');

  readonly trialDaysLeft = computed<number | null>(() => {
    const sub = this.subscription();
    if (!sub) return null;
    if (sub.status !== 'Trialing') return null;
    return sub.daysRemaining ?? daysBetween(sub.trialEndsAt);
  });

  readonly trialColor = computed<TrialProgressionColor>(() => resolveTrialColor(this.trialDaysLeft()));

  readonly shouldShowUrgencyBanner = computed(() =>
    this.isTrialing() && shouldShowUrgencyBanner(this.trialDaysLeft())
  );

  readonly currentPlan = computed<PlanCatalog | null>(() => {
    const sub = this.subscription();
    if (!sub) return null;
    return this.plansStore.findPlan(sub.planSlug) ?? this.plansStore.findPlanByTier(sub.planTier);
  });

  /**
   * Reativação só é permitida APÓS o término da vigência paga. Enquanto a vigência
   * está rolando, o cliente continua usando normalmente — não há o que reativar.
   * Sem currentPeriodEnd assume vigência já encerrada.
   */
  readonly canReactivate = computed<boolean>(() => {
    const sub = this.subscription();
    if (!sub) return false;
    if (sub.status !== 'Canceled') return false;
    if (!sub.currentPeriodEnd) return true;
    const endsAt = new Date(sub.currentPeriodEnd).getTime();
    return !Number.isFinite(endsAt) || endsAt <= Date.now();
  });

  async refresh(): Promise<void> {
    await this.store.refresh();
  }

  async loadInvoices(): Promise<void> {
    await this.store.loadInvoices();
  }

  async performUpgrade(planSlug: PlanSlug, cardToken?: string): Promise<BillingActionResult> {
    try {
      await this.api.upgrade({ planSlug, cardToken });
      await this.store.refresh();
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error as BillingHttpError };
    }
  }

  async performDowngrade(planSlug: PlanSlug): Promise<BillingActionResult> {
    try {
      await this.api.downgrade({ planSlug });
      await this.store.refresh();
      return { ok: true };
    } catch (error) {
      return { ok: false, error: error as BillingHttpError };
    }
  }

  async performCancel(reason: CancellationReason, details?: string): Promise<BillingCancelResult> {
    try {
      const trimmed = details?.trim();
      const response = await this.api.cancel({
        reason,
        ...(trimmed ? { details: trimmed } : {}),
      });
      await this.store.refresh();
      return { ok: true, response };
    } catch (error) {
      return { ok: false, error: error as BillingHttpError };
    }
  }

  async performReactivate(request: ReactivateRequest): Promise<BillingReactivateResult> {
    try {
      const response = await this.api.reactivate({
        paymentMethod: request.paymentMethod,
        ...(request.cardToken ? { cardToken: request.cardToken } : {}),
      });
      await this.store.refresh();
      return { ok: true, response };
    } catch (error) {
      return { ok: false, error: error as BillingHttpError };
    }
  }
}
